import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, googleProvider } from "../firebase/config";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onIdTokenChanged,
  signOut,
} from "firebase/auth";
import api from "../apiClient";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Token refresh interval
  useEffect(() => {
    if (!user || user.needsSignup) return;
    const interval = setInterval(async () => {
      try {
        const idToken = await user.getIdToken(true);
        api.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
        console.debug("[Auth] Token refreshed");
      } catch (err) {
        console.warn("[Auth] Token refresh failed:", err);
        await logout();
      }
    }, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // ✅ Handle Google redirect result (mobile)
  useEffect(() => {
    (async () => {
      const redirectPending = localStorage.getItem("googleRedirectPending");
      if (!redirectPending) {
        setLoading(false);
        return;
      }
      console.log("[Auth] Google redirect flow detected — waiting for Firebase result...");
      try {
        const result = await getRedirectResult(auth);
          localStorage.removeItem("googleRedirectPending");

          if (!result?.user) {
            console.warn("[Auth] No user found after redirect");
            setLoading(false);
            return;
          }

        console.log("[Auth] Google redirect detected and user found:", result.user.email);

        const idToken = await result.user.getIdToken(true);
        api.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;

        // ✅ Check if backend user exists
        const existsRes = await api.post("/auth/check-user", { uid: result.user.uid });

        if (!existsRes.data.exists) {
          // ➕ New Google user → needs extra info
          result.user.needsSignup = true;

          try {
            const inviteCheck = await api.post("/auth/check-invitation", {
              email: result.user.email,
            });
            result.user.invitationInfo = inviteCheck.data;
          } catch (err) {
            console.error("[Auth] Invitation check failed:", err);
            result.user.invitationInfo = { hasInvite: false };
          }

          setUser(result.user);
        } else {
          // ✅ Existing user → fetch profile
          const meRes = await api.get("/api/me");
          result.user.profile = meRes.data;
          result.user.needsSignup = false;
          setUser(result.user);

          // ✅ Auto-redirect to app after successful mobile login
          console.log("[Auth] Redirect login complete → navigating to '/'");
          window.location.replace("/"); // force reload to ensure token applied
        }
      } catch (err) {
        console.error("[Auth] Redirect sign-in error:", err);
        localStorage.removeItem("googleRedirectPending");
      } finally {
        setLoading(false);
      }
    })();
  }, []);


  // ✅ Monitor token changes
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (isAuthenticating) return;

      setIsAuthenticating(true);
      try {
        if (firebaseUser) {
          const providerId = firebaseUser.providerData[0]?.providerId;

          // Skip early signOut for redirect case
          if (
            providerId === "password" &&
            !firebaseUser.emailVerified
          ) {
            console.log("[Auth] Email not verified, signing out");
            await signOut(auth);
            setUser(null);
            delete api.defaults.headers.common["Authorization"];
            setIsAuthenticating(false);
            setLoading(false);
            return;
          }

          const idToken = await firebaseUser.getIdToken(true);
          api.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;

          const existsRes = await api.post("/auth/check-user", { uid: firebaseUser.uid });
          if (existsRes.data.exists) {
            const meRes = await api.get("/api/me");
            firebaseUser.profile = meRes.data;
            firebaseUser.needsSignup = false;
          } else {
            firebaseUser.needsSignup = true;
          }

          setUser(firebaseUser);
        } else {
          setUser(null);
          delete api.defaults.headers.common["Authorization"];
        }
      } catch (err) {
        console.error("[Auth] onIdTokenChanged error:", err);
        setError(err.message);
        setUser(null);
        await signOut(auth);
      } finally {
        setIsAuthenticating(false);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  // ✅ Email/password signup
  const signUp = async (email, password, extraData = {}) => {
    try {
      setLoading(true);
      setError(null);

      const result = await createUserWithEmailAndPassword(auth, email, password);
      try {
        await sendEmailVerification(result.user);
      } catch (verifyErr) {
        console.warn("[Auth] Email verification failed:", verifyErr);
      }

      const idToken = await result.user.getIdToken();
      const response = await api.post("/auth/email-signup", {
        idToken,
        email: result.user.email,
        uid: result.user.uid,
        ...extraData,
      });

      if (response.data.ok) {
        await signOut(auth);
        setUser(null);
        delete api.defaults.headers.common["Authorization"];
        return { emailSent: true };
      }
      throw new Error("Signup failed on backend");
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ✅ Email/password login
  const login = async (email, password) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);

      if (!result.user.emailVerified) {
        await signOut(auth);
        throw new Error("Please verify your email before logging in.");
      }

      const idToken = await result.user.getIdToken();
      api.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;

      const response = await api.post("/auth/email-login", { idToken });
      if (response.data.ok) {
        const meRes = await api.get("/api/me");
        result.user.profile = meRes.data;
      }

      setUser(result.user);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const ua = navigator.userAgent || '';
      const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);

      // Use popup-based sign-in for mobile devices to avoid redirect/getRedirectResult
      // inconsistencies across mobile browsers (Safari, Chrome on iOS, Firefox on iOS, Android WebViews).
      if (isMobile) {
        console.debug('[Auth] Mobile UA detected — using popup fallback for Google sign-in');
        const result = await signInWithPopup(auth, googleProvider);
        const idToken = await result.user.getIdToken(true);
        api.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;

        const existsRes = await api.post("/auth/check-user", { uid: result.user.uid });
        if (!existsRes.data.exists) {
          result.user.needsSignup = true;
          setUser(result.user);
          return { status: "needs-info", googleUser: result.user };
        }

        const meRes = await api.get("/api/me");
        result.user.profile = meRes.data;
        result.user.needsSignup = false;
        setUser(result.user);
        return { status: "ok" };
      }

      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken(true);
      api.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;

      const existsRes = await api.post("/auth/check-user", { uid: result.user.uid });
      if (!existsRes.data.exists) {
        result.user.needsSignup = true;
        setUser(result.user);
        return { status: "needs-info", googleUser: result.user };
      }

      const meRes = await api.get("/api/me");
      result.user.profile = meRes.data;
      result.user.needsSignup = false;
      setUser(result.user);
      return { status: "ok" };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ✅ Complete Google signup
  const completeSignup = async (data) => {
    try {
      setLoading(true);
      const targetUser = user;
      if (!targetUser) throw new Error("No authenticated user");

      const idToken = await targetUser.getIdToken(true);
      const res = await api.post("/auth/google-signup", {
        idToken,
        ...data,
      });

      if (res.data.ok) {
        const meRes = await api.get("/api/me");
        targetUser.profile = meRes.data;
        targetUser.needsSignup = false;
        setUser({ ...targetUser });
        return "ok";
      }
      return "needs-info";
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      delete api.defaults.headers.common["Authorization"];
    } catch (err) {
      setError(err.message);
    }
  };

  // Listen for global API unauthorized events and log out as a safe fallback.
  // This prevents the app from staying in a broken/authenticated-but-invalid state
  // when an API returns 401 during startup or redirect flows.
  useEffect(() => {
    const handleApiUnauthorized = async (e) => {
      console.warn('[Auth] apiUnauthorized event received, logging out');
      try {
        await logout();
      } catch (err) {
        console.error('[Auth] Error during logout after apiUnauthorized', err);
      }
    };

    window.addEventListener('apiUnauthorized', handleApiUnauthorized);
    return () => window.removeEventListener('apiUnauthorized', handleApiUnauthorized);
  }, []);

  const refreshToken = async () => {
    if (!user) return null;
    try {
      const idToken = await user.getIdToken(true);
      api.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;
      return idToken;
    } catch (err) {
      console.error("[Auth] Token refresh failed:", err);
      await logout();
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        signUp,
        login,
        signInWithGoogle,
        completeSignup,
        resetPassword,
        logout,
        refreshToken,
        clearError: () => setError(null),
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
